import React, { useMemo } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  CircularProgress,
  Typography,
  Box,
  Stack,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import { ThemeProvider, createTheme } from "@mui/material/styles";

const modalTheme = createTheme({
  zIndex: { modal: 20000 },
});

const formatAmount = (value) => {
  const num = parseFloat(value) || 0;
  return num.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

const formatDate = (value) => {
  if (!value) return "—";
  const date =
    value instanceof Date
      ? value
      : new Date(typeof value === "string" ? value : String(value));
  if (Number.isNaN(date.getTime())) {
    return String(value);
  }
  return date.toLocaleDateString();
};

const buildSyncStatusInfo = (metadata) => {
  const sync = metadata || {};
  const glEndDate = sync?.gl_end_date ? new Date(sync.gl_end_date) : null;
  const glLastSyncSuccess = sync?.gl_last_sync_success
    ? new Date(sync.gl_last_sync_success)
    : null;
  const glLastSyncAttempt = sync?.gl_last_sync_attempt
    ? new Date(sync.gl_last_sync_attempt)
    : null;
  const validationAttempted = Boolean(sync?.last_validation_attempt);
  const latestPassed = Boolean(sync?.is_synced);

  const today = new Date();
  const lastMonth = new Date(today.getFullYear(), today.getMonth() - 1, 1);
  const lastDayOfLastMonth = new Date(
    lastMonth.getFullYear(),
    lastMonth.getMonth() + 1,
    0
  );

  const syncFailed =
    glLastSyncAttempt &&
    (!glLastSyncSuccess || glLastSyncAttempt > glLastSyncSuccess);

  if (syncFailed) {
    return {
      label: "Sync Issues",
      color: "error",
      reason: "Latest sync attempt failed",
    };
  }

  if (glLastSyncSuccess) {
    const daysSinceSync = Math.floor(
      (today - glLastSyncSuccess) / (1000 * 60 * 60 * 24)
    );
    if (daysSinceSync > 30) {
      return {
        label: "Outdated",
        color: "error",
        reason: `Last synced ${daysSinceSync} days ago (>30 days)`,
      };
    }
  }

  const isCurrentData = glEndDate && glEndDate >= lastDayOfLastMonth;

  if (validationAttempted && !latestPassed) {
    return {
      label: "Sync Issues",
      color: "warning",
      reason: "Validation reported mismatches",
    };
  }

  if (isCurrentData && (!validationAttempted || latestPassed)) {
    return {
      label: "Synced",
      color: "success",
      reason: validationAttempted
        ? "Data is current and validation passed"
        : "Data is current (validation not yet run)",
    };
  }

  if (!isCurrentData && glLastSyncSuccess) {
    const daysSinceSync = Math.floor(
      (today - glLastSyncSuccess) / (1000 * 60 * 60 * 24)
    );
    if (daysSinceSync <= 30) {
      const isNearing = daysSinceSync >= 25;
      return {
        label: "Sync Needed",
        color: isNearing ? "warning" : "success",
        reason: `Data not current. Last synced to ${
          glEndDate ? glEndDate.toLocaleDateString() : "unknown date"
        }${isNearing ? " (approaching 30-day limit)" : ""}`,
      };
    }
  }

  return {
    label: "Not Synced",
    color: "default",
    reason: "No sync history available",
  };
};

const differenceCell = (qb, lucro) => {
  const diff = (parseFloat(qb) || 0) - (parseFloat(lucro) || 0);
  const isZero = Math.abs(diff) < 0.005;
  return (
    <Typography
      component="span"
      sx={{ fontWeight: 600, color: isZero ? "success.main" : "error.main" }}
    >
      {formatAmount(Math.abs(diff))}
    </Typography>
  );
};

const formatAccountLabel = (value) => {
  if (!value) return "";
  const text = String(value).trim();
  const withoutCommas = text.replace(/,/g, " ");
  const singleSpaced = withoutCommas.replace(/\s+/g, " ").trim();
  return singleSpaced.replace(/^(\d{3,6})\s*/, "$1 ");
};

const InfoRow = ({ label, value }) => (
  <Stack direction="row" spacing={1}>
    <Typography variant="body2" sx={{ fontWeight: 600, minWidth: 200 }}>
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Stack>
);

export const ValidationResultModal = ({
  isOpen,
  onClose,
  customer,
  results,
  isLoading,
}) => {
  const metadata = useMemo(
    () => customer?.sync_statuses || {},
    [customer]
  );
  const syncStatusInfo = useMemo(
    () => buildSyncStatusInfo(metadata),
    [metadata]
  );
  const validationErrors = results?.errors || [];
  const validationMismatches = results?.mismatches || [];
  const validationPassed = results?.validation_passed;

  const bsResult = results?.results?.balance_sheet || {};
  const pnlResult = results?.results?.profit_and_loss || {};
  const glResult = results?.results?.general_ledger || {};

  const bsSources = bsResult?.sources || {};
  const dashboardSource = bsSources?.dashboard || bsSources?.report || {};
  const bsMetadata = dashboardSource?.metadata || {};
  const qbTotals = bsMetadata?.qb_totals || {};
  const lucroTotals = bsMetadata?.lucro_totals || {};

  const pnlMetadata = pnlResult?.metadata || {};
  const qbPnlTotals = pnlMetadata?.quickbooks_totals || {};
  const lucroPnlTotals = pnlMetadata?.lucro_totals || {};

  const glMetadata = glResult?.metadata || {};
  const accountsCompared = glMetadata?.total_accounts_compared || 0;
  const tolerance = glMetadata?.tolerance || 0.01;

  const resultsMetadata = results?.metadata || {};
  const qbValidationDate =
    resultsMetadata?.as_of_date ||
    (Array.isArray(glMetadata?.dates_compared)
      ? glMetadata.dates_compared.join(", ")
      : glMetadata?.dates_compared);

  const extraAccounts = [];
  const missingAccounts = [];
  const balanceMismatches = [];

  (validationMismatches || []).forEach((mismatch) => {
    if (mismatch.type === "extra_accounts") {
      extraAccounts.push(...(mismatch.items || []));
    } else if (mismatch.type === "missing_accounts") {
      missingAccounts.push(...(mismatch.items || []));
    } else if (mismatch.type === "balance_mismatch") {
      balanceMismatches.push(mismatch);
    }
  });

  const balanceSheetRows = [
    {
      label: "Assets",
      qb: qbTotals.total_assets,
      lucro: lucroTotals.total_assets,
    },
    {
      label: "Liabilities",
      qb: qbTotals.total_liabilities,
      lucro: lucroTotals.total_liabilities,
    },
    {
      label: "Equity",
      qb: qbTotals.total_equity,
      lucro: lucroTotals.total_equity,
    },
  ];

  const pnlRows = [
    {
      label: "Income",
      qb: qbPnlTotals.total_income,
      lucro: lucroPnlTotals.total_income,
    },
    {
      label: "Expenses",
      qb: qbPnlTotals.total_expenses,
      lucro: lucroPnlTotals.total_expenses,
    },
    {
      label: "Net Income",
      qb: qbPnlTotals.net_income,
      lucro: lucroPnlTotals.net_income,
    },
  ];

  const validationStatusChip = (() => {
    if (!metadata?.last_validation_attempt) {
      return { label: "Not Run", color: "default" };
    }
    if (validationPassed === true) {
      return { label: "Passed", color: "success" };
    }
    if (validationPassed === false) {
      return { label: "Failed", color: "error" };
    }
    return { label: "Pending", color: "warning" };
  })();

  return (
    <ThemeProvider theme={modalTheme}>
      <Dialog
        open={Boolean(isOpen)}
        onClose={onClose}
        maxWidth="lg"
        fullWidth
        scroll="paper"
      >
        <DialogTitle>
          Sync & Validation Report – {customer?.name || "Unknown Customer"}
        </DialogTitle>
        <DialogContent dividers>
          {isLoading ? (
            <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
              <CircularProgress />
            </Box>
          ) : (
            <Stack spacing={4}>
              <Box
                sx={{
                  p: 2,
                  borderRadius: 2,
                  borderLeft: "4px solid #3B82F6",
                  backgroundColor: "rgba(59, 130, 246, 0.08)",
                }}
              >
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  Current Status
                </Typography>
                <Stack direction="row" spacing={2} alignItems="center">
                  <Chip
                    color={syncStatusInfo.color}
                    label={syncStatusInfo.label}
                    size="small"
                  />
                  <Typography variant="body2" color="text.secondary">
                    {syncStatusInfo.reason}
                  </Typography>
                </Stack>
              </Box>

              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Sync Information
                </Typography>
                <Stack spacing={1} sx={{ pl: 1 }}>
                  <InfoRow
                    label="Last Sync Success:"
                    value={
                      metadata?.gl_last_sync_success
                        ? new Date(metadata.gl_last_sync_success).toLocaleString()
                        : "—"
                    }
                  />
                  <InfoRow
                    label="Last Sync Attempt:"
                    value={
                      metadata?.gl_last_sync_attempt
                        ? new Date(metadata.gl_last_sync_attempt).toLocaleString()
                        : "—"
                    }
                  />
                  <InfoRow
                    label="Synced Until:"
                    value={
                      metadata?.gl_end_date
                        ? new Date(metadata.gl_end_date).toLocaleDateString()
                        : "—"
                    }
                  />
                  <InfoRow
                    label="QB GL Data Fetched Until:"
                    value={
                      metadata?.fetch_end_date
                        ? new Date(metadata.fetch_end_date).toLocaleDateString()
                        : "—"
                    }
                  />
                </Stack>
              </Box>

              <Box>
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Validation Information
                </Typography>
                {!metadata?.last_validation_attempt ? (
                  <Typography variant="body2" color="text.secondary" sx={{ pl: 1 }}>
                    No validation has been run yet.
                  </Typography>
                ) : (
                  <Stack spacing={1} sx={{ pl: 1 }}>
                    <Stack
                      direction="row"
                      spacing={2}
                      flexWrap="wrap"
                      alignItems="center"
                    >
                      <InfoRow
                        label="Last Attempt:"
                        value={
                          metadata?.last_validation_attempt
                            ? new Date(metadata.last_validation_attempt).toLocaleString()
                            : "—"
                        }
                      />
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                          Status:
                        </Typography>
                        <Chip
                          label={validationStatusChip.label}
                          color={validationStatusChip.color}
                          size="small"
                        />
                      </Stack>
                      <InfoRow label="Accounts Compared:" value={accountsCompared} />
                      <InfoRow label="Tolerance:" value={tolerance} />
                      <InfoRow
                        label="QuickBooks Comparison Date:"
                        value={formatDate(glMetadata?.comparison_date || qbValidationDate)}
                      />
                    </Stack>
                  </Stack>
                )}
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Balance Sheet Totals
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">QuickBooks</TableCell>
                      <TableCell align="right">Lucro</TableCell>
                      <TableCell align="right">Difference</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {balanceSheetRows.map((row) => (
                      <TableRow key={row.label}>
                        <TableCell>{row.label}</TableCell>
                        <TableCell align="right">{formatAmount(row.qb)}</TableCell>
                        <TableCell align="right">{formatAmount(row.lucro)}</TableCell>
                        <TableCell align="right">
                          {differenceCell(row.qb, row.lucro)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              <Box>
                <Typography variant="subtitle1" sx={{ mb: 1 }}>
                  Profit &amp; Loss Totals
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Category</TableCell>
                      <TableCell align="right">QuickBooks</TableCell>
                      <TableCell align="right">Lucro</TableCell>
                      <TableCell align="right">Difference</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {pnlRows.map((row) => (
                      <TableRow key={row.label}>
                        <TableCell>{row.label}</TableCell>
                        <TableCell align="right">{formatAmount(row.qb)}</TableCell>
                        <TableCell align="right">{formatAmount(row.lucro)}</TableCell>
                        <TableCell align="right">
                          {differenceCell(row.qb, row.lucro)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>

              {balanceMismatches.length > 0 && (
                <Box>
                  <Typography variant="subtitle1" sx={{ mb: 1 }}>
                    Accounts with Balance Differences (&gt;{tolerance} tolerance)
                  </Typography>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Account</TableCell>
                        <TableCell align="right">QuickBooks</TableCell>
                        <TableCell align="right">Lucro</TableCell>
                        <TableCell align="right">Difference</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {balanceMismatches.map((item, idx) => (
                        <TableRow key={`${item.account}-${idx}`}>
                          <TableCell>{item.account}</TableCell>
                          <TableCell align="right">
                            {formatAmount(item.quickbooks_balance)}
                          </TableCell>
                          <TableCell align="right">
                            {formatAmount(item.lucro_balance)}
                          </TableCell>
                          <TableCell align="right">
                            {differenceCell(
                              item.quickbooks_balance,
                              item.lucro_balance
                            )}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              )}

              {(missingAccounts.length > 0 || extraAccounts.length > 0) && (
                <Stack spacing={2}>
                  {missingAccounts.length > 0 && (
                    <Box
                      sx={{
                        border: "1px solid #FCD34D",
                        borderRadius: 2,
                        p: 2,
                        backgroundColor: "rgba(250, 204, 21, 0.12)",
                      }}
                    >
                      <Typography variant="subtitle2" color="#92400E">
                        Missing in Lucro
                      </Typography>
                      <Typography variant="body2" color="#92400E" sx={{ mb: 1 }}>
                        These accounts exist in QuickBooks but not in Lucro. They need to be added or mapped.
                      </Typography>
                      <Box component="ul" sx={{ pl: 3, color: "#92400E" }}>
                      {missingAccounts.map((account, idx) => (
                        <li key={idx}>{formatAccountLabel(account)}</li>
                      ))}
                    </Box>
                  </Box>
                )}
                  {extraAccounts.length > 0 && (
                    <Accordion>
                      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                        <Typography variant="subtitle2">
                          Extra Accounts in Lucro ({extraAccounts.length})
                        </Typography>
                      </AccordionSummary>
                      <AccordionDetails>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          These accounts exist in Lucro but not in QuickBooks. This is informational and doesn&apos;t affect validation.
                        </Typography>
                        <Box component="ul" sx={{ pl: 3 }}>
                        {extraAccounts.map((account, idx) => (
                          <li key={idx}>{formatAccountLabel(account)}</li>
                        ))}
                      </Box>
                    </AccordionDetails>
                  </Accordion>
                  )}
                </Stack>
              )}

              {validationErrors.length > 0 && (
                <Box
                  sx={{
                    border: "1px solid #FECACA",
                    borderRadius: 2,
                    p: 2,
                    backgroundColor: "rgba(254, 226, 226, 0.5)",
                  }}
                >
                  <Typography variant="subtitle2" sx={{ mb: 1 }}>
                    Errors
                  </Typography>
                  <Stack spacing={0.5}>
                    {validationErrors.map((error, idx) => (
                      <Typography key={idx} variant="body2" color="error">
                        • {error}
                      </Typography>
                    ))}
                  </Stack>
                </Box>
              )}

              <Typography variant="caption" color="text.secondary">
                Validation As Of: {formatDate(qbValidationDate)}
              </Typography>

              {results && (
                <Accordion>
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography variant="subtitle2">View Raw JSON Data</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Box
                      component="pre"
                      sx={{
                        backgroundColor: "#111827",
                        color: "#D1D5DB",
                        borderRadius: 1,
                        p: 2,
                        maxHeight: 400,
                        overflow: "auto",
                      }}
                    >
                      {JSON.stringify(results, null, 2)}
                    </Box>
                  </AccordionDetails>
                </Accordion>
              )}
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Close</Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  );
};
